<template>
  <div>  
    <div class="app-user-address">
      <p>Below is the suggested standardized address, suggested updates are highlighted in orange. To continue with the standardized address please click 'Next' <br /> 
        Else, select 'Use entered address' and then click on 'Next'</p>
      <div class="app-user-entered-address">
        <section class="app-physical-address">
          <h2>
            Physical Address
          </h2>
          <div class="app-user-input-address">
            <div class="app-input-selector">
              <input type="radio" id="standardPhysicalAddress" v-model="physicalAddressType" value="standardPhysicalAddress" @change="checkSameAddress()">
              <label for="standardPhysicalAddress">Use standardized address</label>  
            </div>
            <div class="app-input-selector">
              <input type="radio" id="userEnteredPhysicalAddress" v-model="physicalAddressType" value="userEnteredPhysicalAddress" @change="checkSameAddress()"> 
              <label for="userEnteredPhysicalAddress">Use entered address</label> 
            </div>
          </div>
          <div v-if="physicalAddressType == 'userEnteredPhysicalAddress'">
            <UserAddress :addressDetails="userPhysicalAddress" ref="userPhysicalAddressRef"/>
          </div>
          <div v-else>
            <UserAddress :addressDetails="standardPhysicalAddress" ref="userStandardPhysicalAddressRef"/>
          </div>
        </section>
        <section class="app-mailing-address">
          <h2>
            Mailing Address
          </h2>
          <div class="app-user-input-address">
            <div>
              <div class="app-input-selector" v-if="!physicalMailingAddressCheckStore">
                <input type="radio" id="standardMailingAddress" v-model="mailingAddressType" value="standardMailingAddress">
                <label for="standardMailingAddress">Use standardized address</label>  
              </div>
            </div>
            <div>
              <div class="app-input-selector" v-if="!physicalMailingAddressCheckStore">
                <input type="radio" id="userEnteredMailingAddress" v-model="mailingAddressType" value="userEnteredMailingAddress"> 
                <label for="userEnteredMailingAddress">Use entered address</label> 
              </div>
            </div>
          </div>
          <div :class="{'user-same-address': physicalMailingAddressCheckStore}" v-if="mailingAddressType == 'userEnteredMailingAddress'">
            <UserAddress :addressDetails="userMailingAddress" ref="userMailingAddressRef"/> 
          </div>
          <div :class="{'user-same-address': physicalMailingAddressCheckStore}" v-else>
            <UserAddress :addressDetails="standardMailingAddress" ref="userStandardMailingAddressRef"/>
          </div>
        </section>
      </div>
    </div>
    
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <button type="submit" class="app-btn-primary" @click.prevent="handleRouting('next')">
        Next
      </button>
    </BaseButtonGroup>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import UserAddress from './UserAddress.vue';

// Store
const store = useStore();

// Reactive State
const physicalAddressType = ref('standardPhysicalAddress');
const mailingAddressType = ref('standardMailingAddress');

// Computed Properties
const standardPhysicalAddress = computed(() => store.state.guestLogin.standardPhysicalAddress);
const standardMailingAddress = computed(() => store.state.guestLogin.standardMailingAddress);
const userPhysicalAddress = computed(() => store.state.guestLogin.userPhysicalAddress);
const userMailingAddress = computed(() => store.state.guestLogin.userMailingAddress);
const physicalMailingAddressCheckStore = computed(() => store.state.guestLogin.physicalMailingAddressCheck);

const checkUpdatePhysicalAddress = computed(() => {
    const user = userPhysicalAddress.value;
    const standard = standardPhysicalAddress.value;
    return user.addressLine1 == standard.addressLine1 &&
           user.addressLine2 == standard.addressLine2 &&
           user.city == standard.city &&
           user.stateId == standard.stateId &&
           user.zipCode == standard.zipCode &&
           user.zipPlus == standard.zipPlus;
});

const checkUpdateMailingAddress = computed(() => {
    const user = userMailingAddress.value;
    const standard = standardMailingAddress.value;
    return user.addressLine1 == standard.addressLine1 &&
           user.addressLine2 == standard.addressLine2 &&
           user.city == standard.city &&
           user.stateId == standard.stateId &&
           user.zipCode == standard.zipCode &&
           user.zipPlus == standard.zipPlus;
});

// Lifecycle Hook 
onMounted(() => {
    window.scrollTo(0, 0);
    document.body.setAttribute("tabindex", "1");
    document.body.focus();
    document.body.removeAttribute("tabindex");
    document.getElementById("app").scrollTop = 0;

    if (checkUpdatePhysicalAddress.value && checkUpdateMailingAddress.value) {
        store.state.guestLogin.addressUpdateStep = 3;
        store.state.guestLogin.skipStepTo = true;
    } else {
        store.state.guestLogin.skipStepTo = false;
    }
});

// Methods 
const handleRouting = (info) => {
    if (info == 'previous') {
        store.state.guestLogin.addressUpdateStep = 1;
    } else {
        store.state.guestLogin.physicalAddressType = physicalAddressType.value;
        store.state.guestLogin.mailingAddressType = mailingAddressType.value;
        store.state.guestLogin.addressUpdateStep = 3;
    }
};

const checkSameAddress = () => {
    if (physicalMailingAddressCheckStore.value) {
        mailingAddressType.value = physicalAddressType.value == 'userEnteredPhysicalAddress' 
            ? 'userEnteredMailingAddress' 
            : 'standardMailingAddress';
    }
};

</script>


<style scoped lang="scss">
.app-user-address{
  .app-user-entered-address, .app-user-standard-address {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    margin-top: 1rem;
    .app-user-input-address{
      display: flex;
      margin-top: 0.5rem;
      gap: 2rem;
      .input-radio {
        min-width: 100px;
        input[type="radio"] {
          margin: 0.5em 0;
        }
      }
      label {
        margin-top: 0.65em;
      }
    }
    h2{
      font-size: 1rem;
      font-family: "Work Sans", sans-serif;
    }
  }
  .app-user-address {
    .app-user-entered-address {
      .app-mailing-address {
        .user-mailing-address {
          margin-top: 3.5rem !important;
        }
      }
    }
  }
  .user-same-address {
    margin-top: 3.5rem;
  }
}
.app-error-message {
  color: var(--ri-red);
  @extend %font-sans-regular;
  line-height: 1.35em;
  margin: unset;
  padding: 0.5em 0;
  &.--checker {
      margin-left: 1.5em;
  }
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
@media only screen and (max-width: 510px) {
  .app-user-address{
    .app-user-entered-address{
      .app-user-input-address{
        display: block;
        margin-top: 0.5rem;
        gap: 2rem;
        .input-radio {
          min-width: 100px;
          input[type="radio"] {
            margin: 0.5em 0;
          }
        }
        label {
          margin-top: 0.65em;
        }
      }
    }

    .app-user-entered-address, .app-user-standard-address {
      display: block;
      div {
        display: block;
        width: 100%;
      }
      h2 {
        font-size: 1rem;
        font-family: "Work Sans", sans-serif;
      }
      p {
        width: 100%;
      }
    }
  }
}

@media only screen and (max-width: 1100px) {
  .user-same-address{
    margin-top: 0rem !important;
  }
}
</style>
